import { Component, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterLink,
} from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { filter, map, Observable, startWith } from 'rxjs';

interface NavigationItem {
  id: string;
  label: string;
  url: string;
}

const LABEL_RESOLVERS: Record<string, (routeSnapshot: ActivatedRouteSnapshot) => string> = {
  revisions: (s: ActivatedRouteSnapshot) => s.data['Product'].name,
  intakes: (s: ActivatedRouteSnapshot) => s.data['Revision'].revision,
  findings: (s: ActivatedRouteSnapshot) => s.data['Intake'].name,
};

@Component({
  selector: 'comp-navigation',
  templateUrl: 'navigation.component.html',
  imports: [TranslateModule, AsyncPipe, RouterLink],
})
export class NavigationComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  protected readonly navigationItems: Observable<NavigationItem[]> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.buildNavigation(this.route.root)),
  );

  private buildNavigation(
    route: ActivatedRoute,
    url = '',
    breadcrumbs: NavigationItem[] = [],
  ): NavigationItem[] {
    const children = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    const child = children[0];

    if (child) {
      const routeUrl = child.snapshot.url.map((segment) => segment.path).join('/');

      if (routeUrl) {
        url += `/${routeUrl}`;
      }

      const id = child.routeConfig?.data?.['breadcrumbId'];

      if (id) {
        const resolver = LABEL_RESOLVERS[id];
        breadcrumbs.push({
          id,
          label: resolver
            ? resolver(child.snapshot)
            : this.translateService.instant('breadcrumb.' + id),
          url,
        });
      }

      return this.buildNavigation(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }
}
